import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Search, MessageSquare, Plus, Mic, Shield, Upload, FileText, Eye,
  ArrowRight, Cpu, X,
} from "lucide-react";
import { useBranding } from "../../context/BrandingContext";

/**
 * ⌘K / Ctrl-K command palette. Surfaces:
 *   • Static actions (new chat, voice, prompt preview, admin)
 *   • All conversations (jump to)
 *   • All ready files (attach)
 *   • All model aliases (switch active)
 *
 * Parent owns visibility (`open`/`onClose`) and provides handlers + data.
 */
export default function CommandPalette({
  open, onClose, conversations = [], files = [], models = [],
  selectedModelId, isAdmin,
  onNewChat, onOpenVoice, onPreviewPrompt, onOpenAdmin,
  onSelectModel, onAttachFile, onUploadFile, onJumpConversation,
}) {
  const { t } = useTranslation();
  const branding = useBranding();
  const appName = branding.app_name || "Faro";
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQ("");
      setCursor(0);
      // focus next tick so the modal mounts first
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const items = useMemo(() => {
    const list = [];
    const ACTIONS = t("workspace:palette.section.actions");
    list.push({
      group: ACTIONS,
      id: "new-chat",
      icon: Plus,
      label: t("workspace:palette.new_chat"),
      hint: t("workspace:palette.new_chat_hint"),
      run: () => { onNewChat?.(); onClose?.(); },
    });
    list.push({
      group: ACTIONS,
      id: "upload",
      icon: Upload,
      label: t("workspace:palette.upload_file"),
      hint: t("workspace:palette.upload_hint"),
      run: () => { onUploadFile?.(); onClose?.(); },
    });
    list.push({
      group: ACTIONS,
      id: "preview-prompt",
      icon: Eye,
      label: t("workspace:palette.preview_prompt"),
      hint: t("workspace:palette.preview_hint"),
      run: () => { onPreviewPrompt?.(); onClose?.(); },
    });
    if (isAdmin) {
      list.push({
        group: ACTIONS,
        id: "admin",
        icon: Shield,
        label: t("workspace:palette.admin"),
        hint: t("workspace:palette.admin_hint"),
        run: () => { onOpenAdmin?.(); onClose?.(); },
      });
    }
    const SWITCH = t("workspace:palette.section.switch_model");
    const CURRENT = t("workspace:palette.current");
    for (const m of models) {
      list.push({
        group: SWITCH,
        id: `model-${m.id}`,
        icon: Cpu,
        label: t("workspace:palette.use", { name: m.alias_name }),
        hint: m.id === selectedModelId
          ? (m.description ? `${m.description} · ${CURRENT}` : CURRENT)
          : (m.description || ""),
        run: () => { onSelectModel?.(m.id); onClose?.(); },
      });
    }
    const JUMP = t("workspace:palette.section.jump");
    const VOICE_LABEL = t("workspace:palette.voice_call_label");
    const CHAT_LABEL = t("workspace:palette.chat_label");
    const UNTITLED = t("workspace:palette.untitled");
    for (const c of conversations.slice(0, 30)) {
      list.push({
        group: JUMP,
        id: `conv-${c.id}`,
        icon: c.is_voice ? Mic : MessageSquare,
        label: c.title || UNTITLED,
        hint: c.is_voice ? VOICE_LABEL : CHAT_LABEL,
        run: () => { onJumpConversation ? onJumpConversation(c.id) : navigate(`/workspace/${c.id}`); onClose?.(); },
      });
    }
    const ATTACH = t("workspace:palette.section.attach");
    for (const f of files.filter((x) => x.status === "ready").slice(0, 20)) {
      list.push({
        group: ATTACH,
        id: `file-${f.id}`,
        icon: FileText,
        label: f.name,
        hint: t("workspace:palette.attach_to_current", { kind: f.kind }),
        run: () => { onAttachFile?.(f.id); onClose?.(); },
      });
    }
    return list;
  }, [conversations, files, models, selectedModelId, isAdmin, onClose, onNewChat,
      onOpenVoice, onUploadFile, onPreviewPrompt, onOpenAdmin, onSelectModel,
      onJumpConversation, onAttachFile, navigate, t, appName]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) =>
      it.label.toLowerCase().includes(needle) ||
      (it.hint || "").toLowerCase().includes(needle) ||
      it.group.toLowerCase().includes(needle),
    );
  }, [items, q]);

  useEffect(() => { setCursor(0); }, [q]);

  // Group filtered for display
  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((it, idx) => {
      (out[it.group] = out[it.group] || []).push({ ...it, _idx: idx });
    });
    return out;
  }, [filtered]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      filtered[cursor]?.run?.();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-background/70 backdrop-blur-sm flex items-start justify-center pt-[12vh] p-4"
      onClick={onClose}
      data-testid="command-palette"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl border border-border bg-card rounded-sm shadow-2xl overflow-hidden flex flex-col max-h-[70vh]"
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("workspace:palette.placeholder")}
            className="flex-1 bg-transparent border-0 outline-none text-sm"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline text-[10px] font-mono text-muted-foreground border border-border px-1.5 py-0.5 rounded-sm">ESC</kbd>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-0.5" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto" data-testid="command-palette-results">
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t("workspace:palette.no_matches", { q })}
            </div>
          )}
          {Object.entries(grouped).map(([group, rows]) => (
            <div key={group}>
              <div className="px-3 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground">
                {group}
              </div>
              <ul>
                {rows.map((it) => {
                  const active = it._idx === cursor;
                  const Icon = it.icon;
                  return (
                    <li key={it.id}>
                      <button
                        onClick={it.run}
                        onMouseEnter={() => setCursor(it._idx)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition ${
                          active ? "bg-secondary" : "hover:bg-secondary/60"
                        }`}
                        data-testid={`command-item-${it.id}`}
                      >
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="flex-1 truncate">{it.label}</span>
                        <span className="text-[11px] font-mono text-muted-foreground truncate max-w-[40%]">{it.hint}</span>
                        {active && <ArrowRight className="h-3 w-3 text-primary shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-border px-3 py-1.5 text-[10px] font-mono text-muted-foreground flex items-center gap-3">
          <span><kbd className="border border-border px-1 rounded-sm">↑↓</kbd> {t("workspace:palette.foot.navigate")}</span>
          <span><kbd className="border border-border px-1 rounded-sm">↵</kbd> {t("workspace:palette.foot.run")}</span>
          <span><kbd className="border border-border px-1 rounded-sm">esc</kbd> {t("workspace:palette.foot.close")}</span>
        </div>
      </div>
    </div>
  );
}
